import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Film, Plus, Trash2, GripVertical, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import FileDropzone, { VIDEO_UPLOAD } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `video_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
const deleteUploadedFile = async (url) => {
  if (!url?.startsWith('/api/uploads/')) return;
  const filename = url.split('/').pop();
  if (filename) await axios.delete(`${API}/uploads/${encodeURIComponent(filename)}`);
};

const VideosEditor = ({ setProfile }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const saveTimer = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setVideos(res.data.reels || []);
      } catch { toast.error('Failed to load videos'); }
      finally { setLoading(false); }
    })();
  }, []);

  const persistVideos = useCallback(async (updated) => {
    const res = await axios.put(`${API}/profile`, { reels: updated });
    setProfile?.(res.data);
    return res.data;
  }, [setProfile]);

  const saveVideos = useCallback((updated) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await persistVideos(updated);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save videos');
      }
    }, 600);
  }, [persistVideos]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Video must be under 50MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const video = {
        id: createId(),
        url: res.data.url,
        thumbnail_url: res.data.thumbnail_url || '',
        title: title || file.name.replace(/\.[^/.]+$/, ''),
        caption: caption || '',
        type: 'upload',
        platform: 'custom',
        order: videos.length,
        is_active: true,
      };
      const updated = [...videos, video];
      await persistVideos(updated);
      setVideos(updated);
      setTitle('');
      setCaption('');
      setShowAdd(false);
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDroppedVideo = async (upload, file) => {
    const video = {
      id: createId(),
      url: upload.url,
      thumbnail_url: upload.thumbnail_url || '',
      title: title || file.name.replace(/\.[^/.]+$/, ''),
      caption: caption || '',
      type: 'upload',
      platform: 'custom',
      order: videos.length,
      is_active: true,
    };
    const updated = [...videos, video];
    try {
      await persistVideos(updated);
      setVideos(updated);
      setTitle('');
      setCaption('');
      setShowAdd(false);
    } catch (error) {
      await Promise.allSettled([
        deleteUploadedFile(upload.url),
        deleteUploadedFile(upload.thumbnail_url),
      ]);
      throw error;
    }
  };

  const removeVideo = async (id) => {
    const removed = videos.find(v => v.id === id);
    const updated = videos.filter(v => v.id !== id);
    try {
      await persistVideos(updated);
      setVideos(updated);
      await Promise.allSettled([
        deleteUploadedFile(removed?.url),
        deleteUploadedFile(removed?.thumbnail_url),
      ]);
      toast.success('Video removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove video');
    }
  };

  const updateVideo = (id, field, value) => {
    const updated = videos.map(v => v.id === id ? { ...v, [field]: value } : v);
    setVideos(updated);
    saveVideos(updated);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(videos);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const updated = items.map((v, i) => ({ ...v, order: i }));
    setVideos(updated);
    saveVideos(updated);
  };

  if (loading) {
    return <div className="max-w-2xl flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="videos-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Videos</h1>
          <p className="text-[#A1A1AA] mt-1">Upload short videos to showcase on your profile</p>
        </div>
        {videos.length < 10 && (
          <Button data-testid="add-video-btn" onClick={() => setShowAdd(true)}
            className="bg-violet-600 hover:bg-violet-700 text-white gap-2">
            <Plus className="w-4 h-4" /> Add Video
          </Button>
        )}
      </div>

      {/* Add Video Form */}
      {showAdd && (
        <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-6 space-y-4" data-testid="add-video-form">
          <div className="flex items-center justify-between">
            <h2 className="font-outfit font-semibold text-lg text-white">Upload Video</h2>
            <button onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div>
            <Label className="text-[#EDEDED]">Title</Label>
            <Input data-testid="video-title-input" value={title}
              onChange={e => setTitle(e.target.value)}
              className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white"
              placeholder="Give your video a title" />
          </div>

          <div>
            <Label className="text-[#EDEDED]">Caption</Label>
            <Textarea data-testid="video-caption-input" value={caption}
              onChange={e => setCaption(e.target.value)}
              className="mt-1.5 bg-[#1E1E1E] border-[#27272A] text-white resize-none"
              rows={2} placeholder="Optional caption..." />
          </div>

          <div>
            <Label className="text-[#EDEDED]">Video File</Label>
            <FileDropzone
              {...VIDEO_UPLOAD}
              className="mt-1.5 mb-3"
              testId="video-file-dropzone"
              label="Choose a video or drag and drop"
              description="MP4, MOV, WebM, AVI or MKV - max 50MB"
              disabled={uploading}
              onUploaded={handleDroppedVideo}
              successMessage="Video uploaded"
            />
            <input ref={fileInputRef} type="file" accept="video/*" onChange={handleFileUpload} className="hidden" />
            <Button data-testid="video-file-upload-btn" variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-1.5 w-full border-dashed border-[#27272A] text-zinc-400 hover:text-white hover:border-violet-500/50 gap-2 h-24">
              {uploading ? (
                <><div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="w-5 h-5" /> Choose video file (MP4, MOV, WebM — max 50MB)</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Video count */}
      <p className="text-sm text-zinc-500">{videos.length}/10 videos</p>

      {/* Video List */}
      {videos.length === 0 ? (
        <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-12 text-center" data-testid="videos-empty">
          <Film className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">No videos yet</p>
          <p className="text-zinc-600 text-sm mt-1">Upload short videos to showcase on your profile</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="videos-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                {videos.map((video, index) => (
                  <Draggable key={video.id} draggableId={video.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}
                        className={`rounded-2xl bg-[#121212] border p-4 transition-all ${
                          snapshot.isDragging ? 'border-violet-500 shadow-lg shadow-violet-500/10' : 'border-[#27272A]'
                        }`}
                        data-testid={`video-item-${index}`}>
                        <div className="flex items-start gap-3">
                          <div {...provided.dragHandleProps} className="mt-1 cursor-grab text-zinc-600 hover:text-zinc-400">
                            <GripVertical className="w-5 h-5" />
                          </div>

                          {/* Thumbnail */}
                          <div className="w-20 h-14 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0">
                            <video src={resolveMediaUrl(video.url)}
                              className="w-full h-full object-cover" muted preload="metadata" />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <Input value={video.title}
                              onChange={e => updateVideo(video.id, 'title', e.target.value)}
                              className="bg-transparent border-transparent hover:border-[#27272A] focus:border-violet-500/50 text-white text-sm font-medium p-0 h-auto mb-1"
                              placeholder="Video title" />
                            <Input value={video.caption}
                              onChange={e => updateVideo(video.id, 'caption', e.target.value)}
                              className="bg-transparent border-transparent hover:border-[#27272A] focus:border-violet-500/50 text-zinc-400 text-xs p-0 h-auto"
                              placeholder="Caption..." />
                          </div>

                          <button data-testid={`delete-video-${index}`}
                            onClick={() => removeVideo(video.id)}
                            className="text-zinc-600 hover:text-red-400 transition-colors mt-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default VideosEditor;
